import { useOnKeyDown } from '@talos/kyoko';
import { createContext, useCallback, useContext, useState, type PropsWithChildren, type ReactNode } from 'react';
import { useTheme } from 'styled-components';
import { MobileModalSheet } from '../components/Mobile/ModalSheet/MobileModalSheet';
import { MobileModalBackdropWrapper } from '../components/Mobile/ModalSheet/styles';

export interface ModalConfig {
  content?: ReactNode;
  onCloseModal?: () => void;
  onOpenModal?: () => void;
}

interface ModalContextValue {
  openModal: (params?: ModalConfig) => void;
  closeModal: () => void;
  isModalOpen: boolean;
}

const ModalContext = createContext<ModalContextValue | undefined>(undefined);

const DEFAULT_CONFIG: ModalConfig = {};

/**
 * Hook to open modals that are not tied to a specific component.
 * @returns The modal context, which can be used to manage a global modal.
 */
export const useMobileModal = () => {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error('Missing ModalProvider further up in the tree. Did you forget to add it?');
  }
  return context;
};

export const MobileModalProvider = function MobileModalProvider({ children }: PropsWithChildren) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [config, setConfig] = useState<ModalConfig>();
  const theme = useTheme();

  const openModal = useCallback(
    (params?: ModalConfig) => {
      const newConfig = { ...DEFAULT_CONFIG, ...params };
      setConfig(newConfig);
      setIsModalOpen(true);
      newConfig?.onOpenModal?.();
      if (typeof document !== 'undefined') {
        document.body.style.backgroundColor = theme.backgroundBody;
      }
    },
    [theme]
  );

  const onCloseModal = config?.onCloseModal;
  const closeModal = useCallback(() => {
    setConfig(undefined);
    setIsModalOpen(false);
    onCloseModal?.();
    if (typeof document !== 'undefined') {
      document.body.style.backgroundColor = theme.backgroundContent;
    }
  }, [onCloseModal, theme]);

  useOnKeyDown({ key: 'Escape' }, closeModal);

  const contextValue: ModalContextValue = {
    isModalOpen,
    openModal,
    closeModal,
  };

  return (
    <ModalContext.Provider value={contextValue}>
      <MobileModalBackdropWrapper isOpen={isModalOpen}>{children}</MobileModalBackdropWrapper>
      <MobileModalSheet isOpen={isModalOpen} onClose={closeModal}>
        {config?.content}
      </MobileModalSheet>
    </ModalContext.Provider>
  );
};
